import type {
  ApiError,
  CodeLookupResponse,
  CreateGiftIntentRequest,
  CreateGiftIntentResponse,
  GiftStatusResponse,
  RedeemRequest,
  RedeemResponse,
} from '@shared/types';

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const err = (body ?? {}) as Partial<ApiError>;
    throw new ApiRequestError(res.status, err.error ?? `Request failed (${res.status})`, err.code);
  }
  return body as T;
}

export function createGiftIntent(input: CreateGiftIntentRequest): Promise<CreateGiftIntentResponse> {
  return request('/api/gift/intent', { method: 'POST', body: JSON.stringify(input) });
}

export function getGiftStatus(giftId: string, token: string): Promise<GiftStatusResponse> {
  return request(`/api/gift/${encodeURIComponent(giftId)}?t=${encodeURIComponent(token)}`);
}

export function lookupCode(code: string): Promise<CodeLookupResponse> {
  return request(`/api/gift/code/${encodeURIComponent(code)}`);
}

export function redeemGift(input: RedeemRequest): Promise<RedeemResponse> {
  return request('/api/gift/redeem', { method: 'POST', body: JSON.stringify(input) });
}
